import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useToasts } from '../../../contexts/toasts';
import { useFullScreenLoader } from '../../../contexts/fullScreenLoader';
import { RagLoader } from '../../../models/LLM';
import { Alert, Badge, Button, Card, Col, Container, Modal, Row } from 'react-bootstrap';
import AppSearch from '../../../components/common/AppSearch';
import { debounce } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { formatDate } from '../../../utils';
import EditLoaderModal from '../../../components/generics/EditLoaderModal';
import { ChunkMetadata, Llm } from '../../../api/Llm';
import { Flex, Pagination, Tooltip } from 'antd';
import UserItem from '../../../components/common/UserItem';
import { KrakendCollection } from '../../../models/generics';
import { Link } from 'react-router-dom';
import MDEditor from '@uiw/react-md-editor';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { sectionLabelsSelector } from '../../../slices/sectionsSlice';
import AppTag from '../../../components/common/AppTag';
import AppLoader from '../../../components/common/AppLoader';


export default function LoadersList() {
  const [loaders, setLoaders] = useState<RagLoader[]>();
  const [error, setError] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [showChunksModal, setShowChunksModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editing, setEditing] = useState<number>();
  const [chunkIndex, setChunkIndex] = useState<number>(0);
  const [totalChunks, setTotalChunks] = useState<number>();
  const [chunkMetadata, setChunkMetadata] = useState<ChunkMetadata>();
  const [chunkError, setChunkError] = useState(false);
  const [loader, setLoader] = useState<RagLoader>(new RagLoader());

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const loaderLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "loaders"));

  const filteredLoaders = useMemo(() => loaders?.filter(l => l.name.toLowerCase().includes(searchText.toLowerCase())), [loaders, searchText]);

  const initLoaders = useCallback(() => {
    Llm.getLoaders()
      .then(
        ({ data }) => setLoaders((data as KrakendCollection<RagLoader>).collection),
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('loaders-error', message, `${loaderLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
        });
  }, [showToast, loaderLabels]);

  useEffect(() => initLoaders(), [initLoaders]);

  const handleChunkChange = useCallback((page: number, size: number) => setChunkIndex(page), []);
  const openCreateModal = useCallback(() => setShowEditModal(true), []);

  const reset = useCallback(() => {
    setLoaders(undefined);
    initLoaders();
  }, [initLoaders]);

  const handleClose = useCallback(() => {
    setShowChunksModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setTotalChunks(undefined);
    setChunkError(false);
    setChunkIndex(0);
    setLoader(new RagLoader());
    setEditing(undefined);
  }, []);

  const handleSubmit = useCallback((loader: RagLoader) => {
    showLoader();

    const body: RagLoader = {
      ...loader,
      config: loader.config!.map(c => {
        if (c.required && c.value === undefined) c.value = c.default;
        return c;
      }),
    }

    const promise = editing !== undefined
      ? Llm.editLoader(body)
      : Llm.createLoader(body);

    promise.then(
      () => {
        hideLoader();
        showToast('loader-success', `${loaderLabels.singular} ${editing !== undefined ? 'Updated' : 'Created'} successfully`, `${loaderLabels.singular} Success`, 'success');
        handleClose();
        reset();
      },
      (err) => {
        hideLoader();
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast('loaders-error', message, `${loaderLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
      });
  }, [showLoader, hideLoader, showToast, editing, reset, handleClose, loaderLabels]);

  const deleteLoader = useCallback(() => {
    showLoader();

    Llm.deleteLoader(loader!.id as number)
      .then(
        ({ data }) => {
          hideLoader();
          handleClose();
          showToast('loaders-success', `${loaderLabels.singular} deleted successfully`, `${loaderLabels.singular} Success`, 'success');
          reset();
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('loaders-error', message, `${loaderLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
        });
  }, [showLoader, hideLoader, handleClose, loader, reset, showToast, loaderLabels]);

  const handleSearchChange = useCallback((value: string) => setSearchText(value), []);
  const debounceSearchChange = useMemo(() => debounce(handleSearchChange, 500), [handleSearchChange]);

  const testLoader = useCallback((loader: RagLoader) => () => {
    showLoader();

    const body = {
      name: loader.type!,
      config: loader.config!.reduce<Record<string, any>>((acc, curr) => {
        if (curr.value !== undefined) acc[curr.name] = curr.value;
        return acc;
      }, {}),
    };

    Llm.testLoader(body)
      .then(
        ({ data }) => {
          hideLoader();
          data.is_working
            ? showToast('loader-test-success', `${loaderLabels.singular} is working correctly`, `${loaderLabels.singular} Tested!`, 'success')
            : showToast('loader-test-error', data.error, `${loaderLabels.singular} Test Error`, 'danger');
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('loader-test-error', message, `${loaderLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showLoader, hideLoader, showToast, loaderLabels]);

  const handleLoaderEdit = useCallback((loader: RagLoader, index: number) => () => {
    setEditing(index);
    setLoader(loader);
    setShowEditModal(true);
  }, []);

  const handleLoaderDelete = useCallback((loader: RagLoader) => () => {
    setLoader(loader);
    setShowDeleteModal(true);
  }, []);

  const getChunks = useCallback(() => {
    setChunkMetadata(undefined);

    const body = {
      name: loader.type!,
      config: loader.config!.reduce<Record<string, any>>((acc, curr) => {
        if (curr.value !== undefined) acc[curr.name] = curr.value;
        return acc;
      }, {}),
      piece_index: chunkIndex,
      splittingConf: { "chunk_size": 1000, "chunk_overlap": 50 },
    };

    Llm.getLoaderChunks(body)
      .then(
        ({ data }) => {
          setChunkMetadata(data)
          setTotalChunks(data.total_chunks);
        },
        (err) => {
          setChunkError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('loader-chunks-error', message, `${loaderLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showToast, loader, chunkIndex, loaderLabels]);

  const openChunksModal = useCallback((loader: RagLoader) => () => {
    setLoader(loader);
    setShowChunksModal(true);
  }, []);

  useEffect(() => {
    if (loader && showChunksModal) getChunks();
  }, [showChunksModal, chunkIndex, loader, getChunks]);

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert variant="danger" className="py-2">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occurred, please try again later.</span>
      </Alert>
    );

    if (!filteredLoaders) return <AppLoader className="py-5 my-5" justify="center" size={100} />;

    return filteredLoaders.length === 0
      ? (
        <Alert className="py-2" variant="info">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">No {loaderLabels.plural} found.</span>
        </Alert>
      )
      : (
        <Row>
          {filteredLoaders?.map((loader, index) => (
            <Col key={loader.type} md={3} className="px-2">
              <Card className="shadow-sm mb-3">
                <div className="d-flex justify-content-between p-1">
                  <Badge className="shadow w-auto">{loader.accessLevel}</Badge>
                  <Badge bg="none" className="bg-pink shadow w-auto">{loader.type}</Badge>
                </div>
                <Card.Body className="py-2">
                  <Card.Title className="mb-1">
                    <Link to="#" onClick={openChunksModal(loader)}>{loader.name}</Link>
                  </Card.Title>
                  <p className="line-clamp-2 text-secondary mb-2">{loader.description}</p>

                  <Flex className="tags-wrapper mb-3">
                    {loader.tags.length === 0 && <AppTag noTags />}
                    {loader.tags.map(tag => <AppTag key={tag} tag={tag} />)}
                  </Flex>

                  <Row className="justify-content-center">
                    <Tooltip title={`Test ${loaderLabels.singular}`}>
                      <button className="circle-button dark" onClick={testLoader(loader)}>
                        <FontAwesomeIcon icon={solid('cogs')} />
                      </button>
                    </Tooltip>
                    <Tooltip title={`Edit ${loaderLabels.singular}`}>
                      <button className="circle-button dark ms-2" onClick={handleLoaderEdit(loader, index)}>
                        <FontAwesomeIcon icon={solid('pen')} />
                      </button>
                    </Tooltip>
                    <Tooltip title={`Delete ${loaderLabels.singular}`}>
                      <button className="circle-button dark ms-2" onClick={handleLoaderDelete(loader)}>
                        <FontAwesomeIcon icon={solid('trash')} />
                      </button>
                    </Tooltip>
                  </Row>
                </Card.Body>
                <UserItem className="border rounded p-1 m-2" user={loader.owner!} organization={loader.organization!} />
                <small className="ms-1 font-sm text-muted">
                  <b>Created:</b>
                  <span className="ms-1">{formatDate(loader.createDateTime!)}</span>
                </small>
              </Card>
            </Col>
          ))}
        </Row>
      )
  }, [filteredLoaders, loaderLabels, openChunksModal, testLoader, handleLoaderEdit, handleLoaderDelete, error]);

  return (
    <Container className="mt-3">
      <Row className="mb-2 align-items-center">
        <Col>
          <h4 className="mb-0">{loaderLabels.plural} List</h4>
        </Col>

        <Col>
          <AppSearch placeholder={`Search ${loaderLabels.plural}...`} onChange={debounceSearchChange} />
        </Col>

        <Col xs={6} md={4} className="text-end">
          <Button size="sm" variant="success" onClick={openCreateModal}>
            <FontAwesomeIcon icon={solid('plus')} />
            <span className="d-none d-sm-inline ms-2">Create {loaderLabels.singular}</span>
          </Button>
        </Col>
      </Row>

      {renderAsyncContent()}

      <EditLoaderModal loader={loader} open={showEditModal} onClose={handleClose} onSubmit={handleSubmit} />

      <Modal show={showChunksModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>'{loader?.name}' Chunks</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {chunkError
            ? (
              <Alert variant="danger" className="py-2">
                <FontAwesomeIcon icon={solid('exclamation-triangle')} />
                <span className="ms-2">An error occurred, try again later.</span>
              </Alert>
            )
            : (
              <>
                <Flex justify="center" className="mb-2">
                  <Pagination
                    simple
                    current={chunkIndex}
                    total={totalChunks}
                    pageSize={1}
                    onChange={handleChunkChange}
                    showSizeChanger={false}
                    disabled={!chunkMetadata}
                  />
                </Flex>

                {chunkMetadata
                  ? (
                    <div data-color-mode="light">
                      <MDEditor.Markdown className="p-2 border rounded bg-transparent" source={chunkMetadata.chunk} />
                    </div>
                  )
                  : <AppLoader className="py-5" justify="center" size={100} />
                }
              </>
            )
          }
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button variant="light" size="sm" className="border" onClick={handleClose}>Close</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeleteModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete {loaderLabels.singular}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete {loaderLabels.singular} <b>{loader?.name}</b>?</p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleClose}>Close</Button>
          <Button variant="danger" size="sm" onClick={deleteLoader}>Delete</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}